<script>
import {defineComponent, ref, onBeforeMount, nextTick} from "vue";
import Button from "primevue/button";
import DataTable from "primevue/datatable";
import ConfirmationModal from "@/components/modals/ConfirmationModal.vue";
import Column from "primevue/column";
import {useApiCall} from "@/composables/useApiCall";
import {createRoleApiCall, deleteRoleApiCall, loadPermissionsApiCall, loadRolesApiCall, updateRoleApiCall} from "@/services/roles";
import {useI18n} from "vue-i18n";
import InputText from "primevue/inputtext";
import Checkbox from "primevue/checkbox";

export default defineComponent({
  layout: { name: 'AdminLayout' },
  components: {Checkbox, Column, ConfirmationModal, DataTable, Button, InputText},
  setup() {
    const { t } = useI18n()
    const visibleDeleteModal = ref(false)
    const visibleCreateModal = ref(false)
    const deleteId = ref(0)
    const name_ru = ref('')
    const currentRole = ref(null)
    const permissions = ref([])
    const roles = ref([
      {
        id: -1,
        name: "chief",
        name_ru: 'руководитель',
        permissions: [],
      },
      {
        id: -2,
        name: "customer",
        name_ru: 'арендатор',
        permissions: [],
      },
      {
        id: -3,
        name: "owner",
        name_ru: 'собственник',
        permissions: [],
      },
    ])

    const toggleEditModal = (item) => {
      if (item && !item.permissions) {
        item.permissions = []
      }
      currentRole.value = item && item.id > 0 ? {...item, permissions: item.permissions.map(item => item.id)} : null
      if (!currentRole.value && updateError.value) {
        updateError.value = null
      }
    }

    const toggleDeleteModal = (id = 0) => {
      visibleDeleteModal.value = !visibleDeleteModal.value
      if (id) {
        deleteId.value = id
      } else {
        deleteId.value = 0
      }
    }

    const toggleCreateModal = () => {
      visibleCreateModal.value = !visibleCreateModal.value
      if (!visibleCreateModal.value) {
        name_ru.value = ''
        createError.value = null
      }
    }

    const toggleCheckbox = (item) => {
      if (isCheck(item.id)) {
        currentRole.value.permissions = currentRole.value.permissions.filter(item => item.id !== item.id)
      } else {
        currentRole.value.permissions.push(item)
      }
    }

    const isCheck = (id) => {
      return currentRole.value.permissions?.find(item => item.id === id)
    }

    const {
      isLoading: isRolesLoading,
      data: rolesData,
      error: rolesError,
      executeApiCall: getArticlesAction,
    } = useApiCall(loadRolesApiCall, true)

    const {
      isLoading: isPermissionsLoading,
      data: permissionsData,
      error: permissionsError,
      executeApiCall: getPermissionsAction,
    } = useApiCall(loadPermissionsApiCall, true)

    const {
      isLoading: isCreateLoading,
      data: createData,
      error: createError,
      executeApiCall: createAction,
    } = useApiCall(createRoleApiCall, true)

    const {
      isLoading: isDeleteLoading,
      data: deleteData,
      error: deleteError,
      executeApiCall: deleteAction,
    } = useApiCall(deleteRoleApiCall, true)

    const {
      isLoading: isUpdateLoading,
      data: updateData,
      error: updateError,
      executeApiCall: updateAction,
    } = useApiCall(updateRoleApiCall, true)

    const loadRoles = async () => {
      try {
        await getArticlesAction()
        if (rolesData.value) {
          roles.value = [ ...roles.value, ...rolesData.value.data ]
        }
      } catch {
        console.error(rolesError.value)
      }
    }

    const loadPermissions = async () => {
      try {
        await getPermissionsAction()
        if (permissionsData.value) {
          permissions.value = permissionsData.value.data
          roles.value[0].permissions = permissions.value
        }
      } catch {
        console.error(permissionsError.value)
      }
    }


    const deleteItem = async () => {
      try {
        await deleteAction({id: deleteId.value})
        if (deleteData.value) {
          roles.value = roles.value.filter(role => role.id !== deleteId.value)
          toggleDeleteModal(0)
        }
      } catch {
        console.error(deleteError.value)
      }
    }

    const createItem = async () => {
      try {
        await createAction({name_ru: name_ru.value})
        if (createData.value) {
          roles.value.push(createData.value)
          toggleCreateModal()
        }
      } catch {
        console.error(createError.value)
      }
    }

    const updateItem = async () => {
      try {
        await updateAction({id: currentRole.value.id, data: {permissions: currentRole.value.permissions}})
        if (updateData.value) {
          const findIndex = roles.value.findIndex(role => role.id === updateData.value.id)
          if (findIndex !== -1) {
            roles.value[findIndex].permissions = updateData.value.permissions
          }
          toggleEditModal()
        }
      } catch {
        console.error(updateError.value)
      }
    }

    onBeforeMount(async () => {
      await loadPermissions()
      await loadRoles()
      await nextTick(() => {
        window.scrollTo(0, 0)
      })
    })

    return {
      t,
      roles,
      deleteItem,
      createItem,
      updateItem,
      toggleCreateModal,
      isRolesLoading,
      rolesError,
      isPermissionsLoading,
      visibleDeleteModal,
      visibleCreateModal,
      deleteId,
      currentRole,
      toggleEditModal,
      toggleDeleteModal,
      name_ru,
      isCreateLoading,
      isDeleteLoading,
      permissions,
      toggleCheckbox,
      isCheck,
      isUpdateLoading,
      createError,
      updateError,
    };
  }
});
</script>

<template>
  <ConfirmationModal v-model:visible="visibleDeleteModal">
    <template #header>
      <span>Удалить роль</span>
    </template>

    <template #default>
      <span class="flex mt-2">Вы уверены, что хотите удалить роль?</span>
    </template>

    <template #footer>
      <div class="flex justify-content-between mt-4">
        <Button :label="t('labels.cancel')" @click="toggleDeleteModal(0)"
                class="btn-primary-outlined font-light w-12"/>
        <Button :loading="isDeleteLoading" :disabled="isDeleteLoading" :label="t('labels.destroy')" @click="deleteItem"
                class="btn-primary font-light ml-3 w-12"/>
      </div>
    </template>
  </ConfirmationModal>
  <ConfirmationModal v-model:visible="visibleCreateModal" closable @close-modal="toggleCreateModal" class="rules__popup">
    <template #header>
      Название роли
    </template>

    <template #default>
      <div class="roles__popup-wrapper">
       <span class="p-float-label w-full">
         <InputText
             v-model="name_ru"
             id="name_ru"
             class="w-full"
         />
         <label for="name_ru">Введите название роли</label>
       </span>
       <span v-if="createError?.data?.name_ru[0]" class="roles__popup-error color-red">{{createError?.data?.name_ru[0] || ''}}</span>
      </div>
    </template>

    <template #footer>
        <Button :loading="isCreateLoading" :disabled="isCreateLoading || !name_ru" label="Сохранить" @click="createItem"
                class="btn-primary roles__header-btn font-light"/>
    </template>
  </ConfirmationModal>
  <ConfirmationModal v-if="!!currentRole" :visible="!!currentRole" closable @close-modal="toggleEditModal" class="rules__popup --list">
    <template #header>
      Права на редактирование
    </template>

    <template #default>
      <div class="roles__popup-content">
         <div v-for="item in permissions" :key="item.id" class="roles__popup-content-item">
           <Checkbox
               v-model="currentRole.permissions"
               :inputId="item.id.toString()"
               :value="item.id"
               name="permissions"
               class="mr-2"
           />
           <label :for="item.id.toString()">{{ item.type }}</label>
        </div>
      </div>
      <span v-if="updateError?.data?.permissions" class="roles__popup-content-error color-red">{{updateError?.data?.permissions[0]}}</span>
    </template>

    <template #footer>
      <Button :loading="isUpdateLoading" :disabled="isUpdateLoading" label="Сохранить" @click="updateItem"
              class="btn-primary roles__header-btn font-light"/>
    </template>
  </ConfirmationModal>
  <section class="roles__header">
      <h1 class="roles__header-title">Права</h1>
      <div class="flex gap-3">
        <Button label="Создать роль" @click="toggleCreateModal" class="btn-primary roles__header-btn"/>
      </div>
  </section>

  <section class="roles__body">
    <div class="roles__body-table">
      <DataTable :value="roles" :loading="isRolesLoading" showGridlines tableStyle="min-width: 50rem" selectionMode="single">
        <Column field="name_ru" header="Роль">
          <template #body="slotProps">
            <button @click="toggleEditModal(slotProps.data)" class="roles__body-table-name">
              <span>{{ slotProps.data.name_ru }}</span>
            </button>
          </template>
        </Column>

        <Column field="id" header="Доступ к редактированию">
          <template #body="slotProps">
            <div class="roles__body-table-permissions">
              {{ slotProps.data?.permissions ? slotProps.data?.permissions.map(item => item.type).join(', ') : '-' }}
            </div>
          </template>
        </Column>
        <Column field="text" header="Действия">
          <template #body="slotProps">
            <div v-if="slotProps.data.id > 0" class="roles__body-table-actions">
              <button @click="toggleEditModal(slotProps.data)" class="roles__body-table-edit">
                <img src="@/assets/images/icons/Edit.svg" width="24" alt="edit-icon"/>
              </button>
              <button  @click="toggleDeleteModal(slotProps.data.id)" class="roles__body-table-delete">
                <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10.2679 5.36049H14.7322C14.7322 4.76849 14.497 4.20074 14.0784 3.78213C13.6598 3.36352 13.092 3.12835 12.5 3.12835C11.908 3.12835 11.3403 3.36352 10.9217 3.78213C10.503 4.20074 10.2679 4.76849 10.2679 5.36049ZM8.92859 5.36049C8.92859 4.41329 9.30486 3.50488 9.97464 2.83511C10.6444 2.16534 11.5528 1.78906 12.5 1.78906C13.4472 1.78906 14.3556 2.16534 15.0254 2.83511C15.6952 3.50488 16.0714 4.41329 16.0714 5.36049H21.6518C21.8294 5.36049 21.9997 5.43104 22.1253 5.55662C22.2509 5.68221 22.3214 5.85253 22.3214 6.03013C22.3214 6.20773 22.2509 6.37806 22.1253 6.50364C21.9997 6.62923 21.8294 6.69978 21.6518 6.69978H20.4822L19.3956 19.7382C19.3165 20.6867 18.8839 21.5708 18.1835 22.2153C17.4831 22.8598 16.5661 23.2176 15.6143 23.2176H9.38573C8.43394 23.2176 7.51693 22.8598 6.81654 22.2153C6.11615 21.5708 5.68354 20.6867 5.60448 19.7382L4.51787 6.69978H3.34823C3.17063 6.69978 3.00031 6.62923 2.87472 6.50364C2.74914 6.37806 2.67859 6.20773 2.67859 6.03013C2.67859 5.85253 2.74914 5.68221 2.87472 5.55662C3.00031 5.43104 3.17063 5.36049 3.34823 5.36049H8.92859ZM6.9393 19.6266C6.99037 20.2403 7.27022 20.8125 7.72336 21.2296C8.17651 21.6467 8.76985 21.8782 9.38573 21.8783H15.6143C16.2302 21.8782 16.8235 21.6467 17.2767 21.2296C17.7298 20.8125 18.0097 20.2403 18.0607 19.6266L19.1393 6.69978H5.86162L6.9393 19.6266ZM10.4911 9.82478C10.6687 9.82478 10.839 9.89533 10.9646 10.0209C11.0902 10.1465 11.1607 10.3168 11.1607 10.4944V18.0837C11.1607 18.2613 11.0902 18.4316 10.9646 18.5572C10.839 18.6828 10.6687 18.7533 10.4911 18.7533C10.3135 18.7533 10.1432 18.6828 10.0176 18.5572C9.892 18.4316 9.82145 18.2613 9.82145 18.0837V10.4944C9.82145 10.3168 9.892 10.1465 10.0176 10.0209C10.1432 9.89533 10.3135 9.82478 10.4911 9.82478ZM15.1786 10.4944C15.1786 10.3168 15.108 10.1465 14.9825 10.0209C14.8569 9.89533 14.6865 9.82478 14.5089 9.82478C14.3313 9.82478 14.161 9.89533 14.0354 10.0209C13.9099 10.1465 13.8393 10.3168 13.8393 10.4944V18.0837C13.8393 18.2613 13.9099 18.4316 14.0354 18.5572C14.161 18.6828 14.3313 18.7533 14.5089 18.7533C14.6865 18.7533 14.8569 18.6828 14.9825 18.5572C15.108 18.4316 15.1786 18.2613 15.1786 18.0837V10.4944Z" fill="#E26B6B"/>
                </svg>
              </button>
            </div>
          </template>
        </Column>
      </DataTable>
    </div>
  </section>
</template>

<style lang="scss">
.roles__header {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 20px;
  padding-top: 14px;
  margin-bottom: 24px;
}
.roles__header-btn {
  font-weight: 400;
  font-size: 13px;
  line-height: 22px;
  min-width: 228px;
  height: 46px;

  color: white;
  background: #C3987A;
}
.roles__body-table {
  .p-column-header-content  {
    justify-content: start !important;
    .p-column-title {
      font-weight: 600;
      font-size: 18px;
      line-height: 100%;
      text-align: start;
      color: #333333;
    }
  }
}

.roles__body-table-name,
.roles__body-table-permissions {
  font-weight: 400;
  font-size: 18px;
  line-height: 100%;
  text-align: left;
  color: #333333;
}
.roles__body-table-name {
  display: flex;
  justify-content: start;
  text-decoration: underline;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0 4px;
  span::first-letter {
    text-transform: uppercase;
  }
}
.roles__body-table-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
}
.roles__body-table-actions:before {
  position: absolute;
  content: '';
  width: 1px;
  height: 25px;
  background: #E1E8ED;
  top: 50%;
  left: calc(50% - 0.5px);
  transform: translateY(-50%);
  z-index: 2;
}
.roles__body-table-delete,
.roles__body-table-edit {
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
}
.roles__popup-wrapper {
  display: flex;
  flex-direction: column;
  position: relative;
  gap: 10px;
  width: 470px;
  padding-top: 20px;
}
.roles__popup-error {
  min-height: 20px;
}
.rules__popup {
  max-width: unset !important;
  .p-dialog-header-icons {
    position: absolute;
    right: 8px;
    top: 8px;
  }
  .p-dialog-header {
    padding: 20px 20px 16px 20px !important;
  }

  .p-dialog-footer {
    padding: 0 20px 20px;
    justify-content: start;
    text-align: start !important;
  }
  &.--list {
    max-width: 940px !important;
    width: 100%;

    .p-dialog-header {
      padding: 20px !important;
    }

    .p-dialog-content {
      padding: 0 !important;
    }
    .p-dialog-footer {
      padding-top: 20px !important;
    }
    label {
      font-weight: 400;
      font-size: 18px;
      line-height: 100%;
      color: #333333;
      cursor: pointer;
    }
  }
}
.roles__popup-content {
  display: grid;
  background: white;
  gap: 1px;
  grid-template-columns: repeat(3, 1fr);
  border-top: 1px solid #E1E8ED;
  border-bottom: 1px solid #E1E8ED;
}
.roles__popup-content-item {
  padding: 16px 20px;
  background: white;
  outline: 1px solid #E1E8ED;
}
.roles__popup-content-error {
  padding: 16px 20px;
}
</style>